import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import "./usersAdmin.css";

const MODULE_OPTIONS = [
  { value: "POS", label: "POS" },
  { value: "Inventario", label: "Inventario" },
  { value: "Eventos", label: "Eventos" },
  { value: "mensajes_sitio", label: "Mensajes · Tablee" },
  { value: "commerce_products", label: "Comercio · Productos y arriendos compartidos" },
  { value: "tablee_orders", label: "Catálogo compartido (acceso Tablee existente)" },
  { value: "tablee_home", label: "Inicio Tablee" },
  { value: "tablee_landings", label: "Landings SEO Tablee" },
  { value: "tablee_carousel", label: "Carrusel Tablee" },
  { value: "tablee_delivery", label: "Despachos Tablee" },
  { value: "tablee_general_config", label: "Configuración General Tablee" },
  { value: "tablee_payments", label: "Pagos Tablee" },
  { value: "tablee_marketing", label: "Marketing Tablee" },
  { value: "oliva_marketing", label: "Marketing Oliva y Miel" },
  { value: "oliva_sales", label: "Ventas de servicios Oliva y Miel" },
  { value: "oliva_contacts", label: "Mensajes · Oliva y Miel" },
  { value: "tablee_promotions", label: "Códigos promocionales Tablee" },
  { value: "tablee_ecommerce_orders", label: "Ordenes Tablee" },
  { value: "tablee_customers", label: "Usuarios Tablee" },
  { value: "oliva_ecommerce_orders", label: "Oliva y Miel · Órdenes y arriendos" },
  { value: "oliva_customers", label: "Oliva y Miel · Clientes de tienda" },
  { value: "oliva_delivery", label: "Oliva y Miel · Despachos de tienda" },
  { value: "oliva_general_config", label: "Oliva y Miel · Configuración de tienda" },
  { value: "oliva_payments", label: "Oliva y Miel · Pagos de tienda" },
  { value: "oliva_promotions", label: "Oliva y Miel · Códigos promocionales" },
  { value: "user_admin", label: "Usuarios" },
  { value: "cost_control_read", label: "Control de costos · Consultar" },
  { value: "cost_control_register", label: "Control de costos · Registrar" },
  { value: "cost_control_approve_operational", label: "Control de costos · Aprobar operación" },
  { value: "cost_control_approve_finance", label: "Control de costos · Aprobar Finanzas" },
  { value: "cost_control_pay", label: "Control de costos · Registrar pagos" },
  { value: "cost_control_admin", label: "Control de costos · Administrar" },
];

const ROLE_OPTIONS = ["superadmin", "admin", "cashier", "cook", "lead cook"];

const emptyUserForm = {
  username: "",
  name: "",
  role: "cashier",
  googleEmail: "",
  personalEmail: "",
  createdIn: "manual",
  isActive: true,
  modules: [],
  locations: [],
};

async function readJsonResponse(response, fallbackMessage) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(fallbackMessage);
  }
  return response.json();
}

const PRIVILEGED_ROLES = ["admin", "superadmin"];
const isActiveAdmin = (user) => user.isActive !== false && PRIVILEGED_ROLES.includes(user.role);

// Mirrors the API rule (at least one active admin/superadmin) and also keeps the signed-in
// admin from disabling the account they are using. Returns the reason to block, or "".
export function accessLockoutReason(users, currentUsername, username, next) {
  const target = users.find((user) => user.username === username);
  if (!target || target.isActive === false) return "";
  const nextActive = next.isActive !== false;
  if (username === currentUsername && !nextActive) return "No puedes desactivar la cuenta con la que estás conectado.";
  if (!isActiveAdmin(target) || (nextActive && PRIVILEGED_ROLES.includes(next.role ?? target.role))) return "";
  return users.some((user) => user.username !== username && isActiveAdmin(user))
    ? ""
    : "Debe quedar al menos un administrador activo.";
}

function getSessionUsername() {
  try {
    return JSON.parse(localStorage.getItem("sessionData") || "{}").username || "";
  } catch (_error) {
    return "";
  }
}

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [form, setForm] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const [modalTab, setModalTab] = useState("data");
  const [moduleToAdd, setModuleToAdd] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [isModuleDropdownOpen, setIsModuleDropdownOpen] = useState(false);
  const [moduleDropdownRect, setModuleDropdownRect] = useState(null);
  const [locationToAdd, setLocationToAdd] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationDropdownRect, setLocationDropdownRect] = useState(null);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleDropdownRect, setRoleDropdownRect] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const sessionUsername = getSessionUsername();
  const moduleSelectTriggerRef = useRef(null);
  const locationSelectTriggerRef = useRef(null);
  const roleSelectTriggerRef = useRef(null);

  const selectedUser = useMemo(
    () => users.find((user) => user.username === selectedUsername) || null,
    [users, selectedUsername]
  );
  const visibleUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => (
      [user.name, user.username, user.role, user.googleEmail, user.personalEmail, user.createdIn]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    ));
  }, [query, users]);
  const availableModules = useMemo(() => (
    MODULE_OPTIONS.filter((module) => !form?.modules?.includes(module.value))
  ), [form?.modules]);
  const selectedModuleToAdd = useMemo(() => (
    availableModules.find((module) => module.value === moduleToAdd) || null
  ), [availableModules, moduleToAdd]);
  const filteredAvailableModules = useMemo(() => {
    const needle = moduleSearch.trim().toLowerCase();
    if (!needle) return availableModules;
    return availableModules.filter((module) => (
      [module.label, module.value]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    ));
  }, [availableModules, moduleSearch]);
  const selectedModules = useMemo(() => (
    MODULE_OPTIONS.filter((module) => form?.modules?.includes(module.value))
  ), [form?.modules]);
  const availableLocations = useMemo(() => (
    locations.filter((location) => !form?.locations?.includes(location.id))
  ), [form?.locations, locations]);
  const selectedLocationToAdd = useMemo(() => (
    availableLocations.find((location) => String(location.id) === String(locationToAdd)) || null
  ), [availableLocations, locationToAdd]);
  const filteredAvailableLocations = useMemo(() => {
    const needle = locationSearch.trim().toLowerCase();
    if (!needle) return availableLocations;
    return availableLocations.filter((location) => (
      [location.name, location.id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    ));
  }, [availableLocations, locationSearch]);
  const selectedLocations = useMemo(() => (
    locations.filter((location) => form?.locations?.includes(location.id))
  ), [form?.locations, locations]);

  const loadData = useCallback(async () => {
    try {
      const headers = getSessionHeaders();
      const [usersResponse, locationsResponse] = await Promise.all([
        fetch(apiUrl("/user/admin/users"), { headers }),
        fetch(apiUrl("/locations/all"), { headers }),
      ]);
      const usersData = await readJsonResponse(
        usersResponse,
        "La API de usuarios no esta disponible. Reinicia el API para cargar los endpoints nuevos."
      );
      const locationsData = await readJsonResponse(
        locationsResponse,
        "La API de ubicaciones no esta disponible."
      );

      if (usersData.success) {
        setUsers(usersData.users || []);
      } else {
        setMessage(usersData.message || "No se pudieron cargar los usuarios.");
      }

      if (locationsData.success) setLocations(locationsData.locations || []);
    } catch (err) {
      setMessage(err.message || "No se pudieron cargar los usuarios.");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isEditModalOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key !== "Escape") return;
      if (isModuleDropdownOpen) {
        setIsModuleDropdownOpen(false);
        setModuleSearch("");
        setModuleDropdownRect(null);
        return;
      }
      if (isLocationDropdownOpen) {
        setIsLocationDropdownOpen(false);
        setLocationSearch("");
        setLocationDropdownRect(null);
        return;
      }
      if (isRoleDropdownOpen) {
        setIsRoleDropdownOpen(false);
        setRoleDropdownRect(null);
        return;
      }
      setIsEditModalOpen(false);
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditModalOpen, isModuleDropdownOpen, isLocationDropdownOpen, isRoleDropdownOpen]);

  useEffect(() => {
    if (!isModuleDropdownOpen && !isLocationDropdownOpen && !isRoleDropdownOpen) return undefined;

    function handleOutsideClick(event) {
      if (
        !event.target.closest(".users-admin__module-select") &&
        !event.target.closest(".users-admin__module-select-menu")
      ) {
        setIsModuleDropdownOpen(false);
        setModuleSearch("");
        setModuleDropdownRect(null);
      }
      if (
        !event.target.closest(".users-admin__module-select") &&
        !event.target.closest(".users-admin__module-select-menu")
      ) {
        setIsLocationDropdownOpen(false);
        setLocationSearch("");
        setLocationDropdownRect(null);
      }
      if (
        !event.target.closest(".users-admin__module-select") &&
        !event.target.closest(".users-admin__module-select-menu")
      ) {
        setIsRoleDropdownOpen(false);
        setRoleDropdownRect(null);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isModuleDropdownOpen, isLocationDropdownOpen, isRoleDropdownOpen]);

  useEffect(() => {
    if (!isModuleDropdownOpen || !moduleSelectTriggerRef.current) return undefined;

    function updateDropdownRect() {
      const rect = moduleSelectTriggerRef.current.getBoundingClientRect();
      setModuleDropdownRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateDropdownRect();
    window.addEventListener("resize", updateDropdownRect);
    window.addEventListener("scroll", updateDropdownRect, true);
    return () => {
      window.removeEventListener("resize", updateDropdownRect);
      window.removeEventListener("scroll", updateDropdownRect, true);
    };
  }, [isModuleDropdownOpen]);

  useEffect(() => {
    if (!isLocationDropdownOpen || !locationSelectTriggerRef.current) return undefined;

    function updateDropdownRect() {
      const rect = locationSelectTriggerRef.current.getBoundingClientRect();
      setLocationDropdownRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateDropdownRect();
    window.addEventListener("resize", updateDropdownRect);
    window.addEventListener("scroll", updateDropdownRect, true);
    return () => {
      window.removeEventListener("resize", updateDropdownRect);
      window.removeEventListener("scroll", updateDropdownRect, true);
    };
  }, [isLocationDropdownOpen]);

  useEffect(() => {
    if (!isRoleDropdownOpen || !roleSelectTriggerRef.current) return undefined;

    function updateDropdownRect() {
      const rect = roleSelectTriggerRef.current.getBoundingClientRect();
      setRoleDropdownRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateDropdownRect();
    window.addEventListener("resize", updateDropdownRect);
    window.addEventListener("scroll", updateDropdownRect, true);
    return () => {
      window.removeEventListener("resize", updateDropdownRect);
      window.removeEventListener("scroll", updateDropdownRect, true);
    };
  }, [isRoleDropdownOpen]);

  function openEditModal(username) {
    setMessage("");
    setModalMode("edit");
    setModalTab("data");
    setModuleToAdd("");
    setModuleSearch("");
    setIsModuleDropdownOpen(false);
    setLocationToAdd("");
    setLocationSearch("");
    setIsLocationDropdownOpen(false);
    setIsRoleDropdownOpen(false);
    setSelectedUsername(username);
    setIsEditModalOpen(true);
  }

  function openCreateModal() {
    setMessage("");
    setModalMode("create");
    setModalTab("data");
    setModuleToAdd("");
    setModuleSearch("");
    setIsModuleDropdownOpen(false);
    setLocationToAdd("");
    setLocationSearch("");
    setIsLocationDropdownOpen(false);
    setIsRoleDropdownOpen(false);
    setSelectedUsername("");
    setForm(emptyUserForm);
    setIsEditModalOpen(true);
  }

  function closeEditModal() {
    setIsModuleDropdownOpen(false);
    setModuleSearch("");
    setIsLocationDropdownOpen(false);
    setLocationSearch("");
    setIsEditModalOpen(false);
    setIsRoleDropdownOpen(false);
    setRoleDropdownRect(null);
  }

  function closeAccessDropdowns() {
    setIsModuleDropdownOpen(false);
    setModuleSearch("");
    setModuleDropdownRect(null);
    setIsLocationDropdownOpen(false);
    setLocationSearch("");
    setLocationDropdownRect(null);
    setIsRoleDropdownOpen(false);
    setRoleDropdownRect(null);
  }

  function changeModalTab(nextTab) {
    closeAccessDropdowns();
    setModalTab(nextTab);
  }

  useEffect(() => {
    if (modalMode === "create") return;
    if (!selectedUser) {
      setForm(null);
      return;
    }

    setForm({
      name: selectedUser.name || "",
      role: selectedUser.role || "",
      googleEmail: selectedUser.googleEmail || "",
      personalEmail: selectedUser.personalEmail || "",
      createdIn: selectedUser.createdIn || "",
      isActive: selectedUser.isActive !== false,
      modules: selectedUser.modules || [],
      locations: selectedUser.locations || [],
    });
  }, [modalMode, selectedUser]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleSwitchKeyDown(event, onToggle) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }

  function handleSwitchPointerUp(event, onToggle) {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }

  function addModuleAccess() {
    if (!moduleToAdd) return;
    setForm((current) => {
      if (current.modules.includes(moduleToAdd)) return current;
      return { ...current, modules: [...current.modules, moduleToAdd] };
    });
    setModuleToAdd("");
    setModuleSearch("");
    setIsModuleDropdownOpen(false);
  }

  function removeModuleAccess(moduleValue) {
    setForm((current) => ({
      ...current,
      modules: current.modules.filter((module) => module !== moduleValue),
    }));
  }

  function confirmRemoveModuleAccess(module) {
    setConfirmDialog({
      title: "Eliminar módulo",
      message: `Eliminar "${module.label}" de los módulos de este usuario?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removeModuleAccess(module.value);
        setConfirmDialog(null);
      },
    });
  }

  function addLocationAccess() {
    const locationId = Number(locationToAdd);
    if (!Number.isInteger(locationId) || locationId <= 0) return;
    setForm((current) => {
      if (current.locations.includes(locationId)) return current;
      return { ...current, locations: [...current.locations, locationId] };
    });
    setLocationToAdd("");
    setLocationSearch("");
    setIsLocationDropdownOpen(false);
  }

  function removeLocationAccess(locationId) {
    setForm((current) => ({
      ...current,
      locations: current.locations.filter((id) => id !== locationId),
    }));
  }

  function confirmRemoveLocationAccess(location) {
    setConfirmDialog({
      title: "Eliminar ubicación",
      message: `Eliminar "${location.name}" de las ubicaciones permitidas para este usuario?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removeLocationAccess(location.id);
        setConfirmDialog(null);
      },
    });
  }

  async function saveUser(event) {
    event.preventDefault();
    if (!form || (modalMode === "edit" && !selectedUser)) return;

    setMessage("");
    const lockout = modalMode === "edit"
      ? accessLockoutReason(users, getSessionUsername(), selectedUser.username, { role: form.role, isActive: form.isActive })
      : "";
    if (lockout) {
      setMessage(lockout);
      return;
    }
    let data;
    try {
      const response = await fetch(apiUrl(modalMode === "create" ? "/user/admin/users" : `/user/admin/users/${selectedUser.username}`), {
        method: modalMode === "create" ? "POST" : "PATCH",
        headers: getSessionHeaders(),
        body: JSON.stringify(form),
      });
      data = await readJsonResponse(
        response,
        "La API de usuarios no esta disponible. Reinicia el API para cargar los endpoints nuevos."
      );
    } catch (err) {
      setMessage(err.message || "No se pudo guardar el usuario.");
      return;
    }

    if (!data.success) {
      setMessage(data.message || "No se pudo guardar el usuario.");
      return;
    }

    if (modalMode === "create") {
      setUsers((current) => [...current, data.user].sort((a, b) => a.username.localeCompare(b.username)));
      setSelectedUsername(data.user.username);
      setModalMode("edit");
      setMessage(data.claimSent === false ? (data.message || "Usuario creado, pero no se pudo enviar el claim.") : "Usuario creado. Se envió el claim de acceso.");
      return;
    }

    setUsers((current) => current.map((user) => user.username === data.user.username ? data.user : user));
    setMessage("Usuario actualizado.");
  }

  async function toggleUserActive(user) {
    if (!user) return;
    const nextActive = user.isActive === false;
    const lockout = accessLockoutReason(users, getSessionUsername(), user.username, { isActive: nextActive });
    if (lockout) {
      setMessage(lockout);
      return;
    }
    setMessage("");
    const restore = () => {
      setUsers((current) => current.map((item) => (item.username === user.username ? user : item)));
      if (selectedUsername === user.username) {
        setForm((current) => current ? { ...current, isActive: user.isActive !== false } : current);
      }
    };
    setUsers((current) => current.map((item) => (
      item.username === user.username ? { ...item, isActive: nextActive } : item
    )));
    if (selectedUsername === user.username) {
      setForm((current) => current ? { ...current, isActive: nextActive } : current);
    }

    let data;
    try {
      const response = await fetch(apiUrl(`/user/admin/users/${user.username}/active`), {
        method: "PATCH",
        headers: getSessionHeaders(),
        body: JSON.stringify({ isActive: nextActive }),
      });
      data = await readJsonResponse(
        response,
        "La API de usuarios no esta disponible. Reinicia el API para cargar los endpoints nuevos."
      );
    } catch (err) {
      restore();
      setMessage(err.message || "No se pudo actualizar el estado del usuario.");
      return;
    }

    if (!data.success) {
      restore();
      setMessage(data.message || "No se pudo actualizar el estado del usuario.");
      return;
    }

    setUsers((current) => current.map((item) => item.username === data.user.username ? data.user : item));
    if (selectedUsername === data.user.username) {
      setForm((current) => current ? { ...current, isActive: data.user.isActive !== false } : current);
    }
  }

  function deleteUser(user) {
    if (!user) return;
    setConfirmDialog({
      title: "Eliminar acceso",
      message: `Eliminar acceso de ${user.name || user.username}? Esta accion no elimina ventas ni movimientos historicos.`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteUser(user),
    });
  }

  async function confirmDeleteUser(user) {
    setConfirmDialog(null);
    setMessage("");
    let data;
    try {
      const response = await fetch(apiUrl(`/user/admin/users/${user.username}`), {
        method: "DELETE",
        headers: getSessionHeaders(),
      });
      data = await readJsonResponse(
        response,
        "La API de usuarios no esta disponible. Reinicia el API para cargar los endpoints nuevos."
      );
    } catch (err) {
      setMessage(err.message || "No se pudo eliminar el usuario.");
      return;
    }

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar el usuario.");
      return;
    }

    setUsers((current) => current.filter((item) => item.username !== data.username));
    setSelectedUsername((current) => (current === data.username ? "" : current));
    setIsEditModalOpen(false);
    setMessage("Usuario eliminado del acceso activo.");
  }

  const moduleDropdownMenu = isModuleDropdownOpen && moduleDropdownRect ? createPortal(
    <div
      className="users-admin__module-select-menu"
      role="listbox"
      style={{ left: moduleDropdownRect.left, top: moduleDropdownRect.top, width: moduleDropdownRect.width }}
    >
      <label className="users-admin__module-select-search">
        <span>Buscar módulo</span>
        <input
          value={moduleSearch}
          placeholder="Nombre del módulo"
          autoFocus
          onChange={(event) => setModuleSearch(event.target.value)}
        />
      </label>
      <div className="users-admin__module-select-options">
        {filteredAvailableModules.length ? filteredAvailableModules.map((module) => (
          <button
            key={module.value}
            type="button"
            role="option"
            aria-selected={module.value === moduleToAdd}
            className={module.value === moduleToAdd ? "is-selected" : ""}
            onClick={() => {
              setModuleToAdd(module.value);
              setModuleSearch("");
              setIsModuleDropdownOpen(false);
            }}
          >
            <strong>{module.label}</strong>
            <small>{module.value}</small>
          </button>
        )) : (
          <span className="users-admin__module-select-empty">Sin módulos disponibles</span>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  const moduleSelectLabel = selectedModuleToAdd?.label || "Seleccionar módulo";
  const locationDropdownMenu = isLocationDropdownOpen && locationDropdownRect ? createPortal(
    <div
      className="users-admin__module-select-menu"
      role="listbox"
      style={{ left: locationDropdownRect.left, top: locationDropdownRect.top, width: locationDropdownRect.width }}
    >
      <label className="users-admin__module-select-search">
        <span>Buscar ubicación</span>
        <input
          value={locationSearch}
          placeholder="Nombre de ubicación"
          autoFocus
          onChange={(event) => setLocationSearch(event.target.value)}
        />
      </label>
      <div className="users-admin__module-select-options">
        {filteredAvailableLocations.length ? filteredAvailableLocations.map((location) => (
          <button
            key={location.id}
            type="button"
            role="option"
            aria-selected={String(location.id) === String(locationToAdd)}
            className={String(location.id) === String(locationToAdd) ? "is-selected" : ""}
            onClick={() => {
              setLocationToAdd(String(location.id));
              setLocationSearch("");
              setIsLocationDropdownOpen(false);
            }}
          >
            <strong>{location.name}</strong>
            <small>ID {location.id}</small>
          </button>
        )) : (
          <span className="users-admin__module-select-empty">Sin ubicaciones disponibles</span>
        )}
      </div>
    </div>,
    document.body
  ) : null;
  const locationSelectLabel = selectedLocationToAdd?.name || "Seleccionar ubicación";
  const roleDropdownMenu = isRoleDropdownOpen && roleDropdownRect ? createPortal(
    <div
      className="users-admin__module-select-menu users-admin__module-select-menu--compact"
      role="listbox"
      style={{ left: roleDropdownRect.left, top: roleDropdownRect.top, width: roleDropdownRect.width }}
    >
      <div className="users-admin__module-select-options">
        {ROLE_OPTIONS.map((role) => (
          <button
            key={role}
            type="button"
            role="option"
            aria-selected={role === form?.role}
            className={role === form?.role ? "is-selected" : ""}
            onClick={() => {
              updateField("role", role);
              setIsRoleDropdownOpen(false);
              setRoleDropdownRect(null);
            }}
          >
            <strong>{role}</strong>
          </button>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="users-admin">
      <header className="users-admin__header">
        <div>
          <span>Global</span>
          <h1>Usuarios</h1>
          <p>Administra datos globales de acceso, módulos y ubicaciones.</p>
        </div>
      </header>

      <section className="users-admin__table-panel">
        <div className="users-admin__table-toolbar">
          <div>
            <button type="button" className="users-admin__add-button" onClick={openCreateModal}>
              <span className="material-symbols-outlined">add</span>
              Agregar usuario
            </button>
          </div>
          <label>
            Buscar
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nombre, usuario, rol o email" />
          </label>
        </div>
        <div className="users-admin__table-wrap">
          <table className="users-admin__table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Email OVM</th>
                <th>Origen</th>
                <th>Módulos</th>
                <th>Ubicaciones</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => {
                const toggleLockout = user.isActive !== false
                  ? accessLockoutReason(users, sessionUsername, user.username, { isActive: false })
                  : "";
                return (
                <tr key={user.username} className={isEditModalOpen && user.username === selectedUser?.username ? "is-active" : ""}>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.name}</td>
                  <td><span className="users-admin__pill">{user.role}</span></td>
                  <td>{user.googleEmail || "Sin email"}</td>
                  <td>{user.createdIn || "Sin origen"}</td>
                  <td>{user.modules?.length || 0}</td>
                  <td>{user.locations?.length || 0}</td>
                  <td>
                    <button
                      type="button"
                      className={`users-admin__switch ${user.isActive !== false ? "is-on" : ""}`}
                      onPointerUp={(event) => handleSwitchPointerUp(event, () => toggleUserActive(user))}
                      onKeyDown={(event) => handleSwitchKeyDown(event, () => toggleUserActive(user))}
                      aria-pressed={user.isActive !== false}
                      aria-label={`${user.isActive !== false ? "Desactivar" : "Activar"} ${user.username}`}
                      disabled={Boolean(toggleLockout)}
                      title={toggleLockout || undefined}
                    >
                      <span />
                    </button>
                  </td>
                  <td>
                    <div className="users-admin__actions">
                      <button type="button" className="is-icon" onClick={() => openEditModal(user.username)} aria-label={`Editar ${user.username}`}>
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button type="button" className="is-danger is-icon" onClick={() => deleteUser(user)} aria-label={`Eliminar ${user.username}`}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {visibleUsers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="users-admin__empty">No hay usuarios para mostrar.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {isEditModalOpen && form && (modalMode === "create" || selectedUser) ? (
        <div className="users-admin__modal-backdrop" role="presentation" onMouseDown={closeEditModal}>
          <form className="users-admin__detail users-admin__modal" onSubmit={saveUser} onMouseDown={(event) => event.stopPropagation()}>
            <div className="users-admin__detail-head">
              <div>
                <span>{modalMode === "create" ? "Nuevo acceso" : selectedUser.username}</span>
                <h2>{modalMode === "create" ? "Agregar usuario" : selectedUser.name}</h2>
              </div>
              <div className="users-admin__detail-actions">
                <button type="button" className="users-admin__close-button" onClick={closeEditModal} aria-label="Cerrar">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>

            <div className="users-admin__modal-tabs" role="tablist" aria-label="Secciones del usuario">
              <span className={`users-admin__modal-tabs-thumb is-${modalTab}`} />
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === "data"}
                className={modalTab === "data" ? "is-active" : ""}
                onClick={() => changeModalTab("data")}
              >
                Datos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === "modules"}
                className={modalTab === "modules" ? "is-active" : ""}
                onClick={() => changeModalTab("modules")}
              >
                Módulos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === "locations"}
                className={modalTab === "locations" ? "is-active" : ""}
                onClick={() => changeModalTab("locations")}
              >
                Ubicaciones
              </button>
            </div>

            <div className="users-admin__modal-body">
              {modalTab === "data" ? (
                <div className="users-admin__fields">
                  {modalMode === "create" ? (
                    <label>
                      Usuario
                      <input value={form.username} onChange={(event) => updateField("username", event.target.value)} placeholder="nombre.apellido" />
                    </label>
                  ) : null}
                <label>
                  Nombre
                  <input value={form.name} onChange={(event) => updateField("name", event.target.value)} />
                </label>
                  <label>
                    Rol
                    <div className="users-admin__module-select">
                      <button
                        ref={roleSelectTriggerRef}
                        type="button"
                        className="users-admin__module-select-trigger"
                        aria-haspopup="listbox"
                        aria-expanded={isRoleDropdownOpen}
                        onClick={() => {
                          setIsModuleDropdownOpen(false);
                          setModuleSearch("");
                          setIsLocationDropdownOpen(false);
                          setLocationSearch("");
                          setIsRoleDropdownOpen((open) => !open);
                        }}
                      >
                        <span>{form.role || "Seleccionar rol"}</span>
                        <span className="material-symbols-outlined">expand_more</span>
                      </button>
                    </div>
                  </label>
                  <label>
                    Email OVM
                    <input type="email" value={form.googleEmail} onChange={(event) => updateField("googleEmail", event.target.value)} placeholder="usuario@olivaymiel.cl" />
                  </label>
                  <label>
                    Email personal
                    <input type="email" value={form.personalEmail} onChange={(event) => updateField("personalEmail", event.target.value)} placeholder="correo personal" />
                  </label>
                  <label>
                    Creado en
                    <input value={form.createdIn} onChange={(event) => updateField("createdIn", event.target.value)} />
                  </label>
                </div>
              ) : modalTab === "modules" ? (
                <div className="users-admin__access-grid">
                  <section className="users-admin__access-panel">
                    <div className="users-admin__access-head">
                      <div>
                        <span>Permisos</span>
                        <h3>Módulos</h3>
                      </div>
                      <div className="users-admin__access-picker">
                        <div className="users-admin__module-select">
                          <button
                            ref={moduleSelectTriggerRef}
                            type="button"
                            className="users-admin__module-select-trigger"
                            aria-haspopup="listbox"
                            aria-expanded={isModuleDropdownOpen}
                            onClick={() => {
                              setIsLocationDropdownOpen(false);
                              setLocationSearch("");
                              setIsModuleDropdownOpen((open) => !open);
                            }}
                          >
                            <span className={selectedModuleToAdd ? "" : "is-placeholder"}>{moduleSelectLabel}</span>
                            <span className="material-symbols-outlined">expand_more</span>
                          </button>
                        </div>
                        <button type="button" onClick={addModuleAccess} disabled={!moduleToAdd} aria-label="Agregar módulo">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="users-admin__access-table">
                      <div className="users-admin__access-row users-admin__access-row--head">
                        <span>Módulo</span>
                        <span>Acciones</span>
                      </div>
                      {selectedModules.map((module) => (
                        <div className="users-admin__access-row" key={module.value}>
                          <strong>{module.label}</strong>
                          <button type="button" className="users-admin__access-delete" onClick={() => confirmRemoveModuleAccess(module)} aria-label={`Eliminar ${module.label}`}>
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      ))}
                      {!selectedModules.length ? <p>No hay módulos agregados.</p> : null}
                    </div>
                  </section>
                </div>
              ) : (
                <div className="users-admin__access-grid">
                  <section className="users-admin__access-panel">
                    <div className="users-admin__access-head">
                      <div>
                        <span>Acceso territorial</span>
                        <h3>Ubicaciones permitidas</h3>
                      </div>
                      <div className="users-admin__access-picker">
                        <div className="users-admin__module-select">
                          <button
                            ref={locationSelectTriggerRef}
                            type="button"
                            className="users-admin__module-select-trigger"
                            aria-haspopup="listbox"
                            aria-expanded={isLocationDropdownOpen}
                            onClick={() => {
                              setIsModuleDropdownOpen(false);
                              setModuleSearch("");
                              setIsLocationDropdownOpen((open) => !open);
                            }}
                          >
                            <span className={selectedLocationToAdd ? "" : "is-placeholder"}>{locationSelectLabel}</span>
                            <span className="material-symbols-outlined">expand_more</span>
                          </button>
                        </div>
                        <button type="button" onClick={addLocationAccess} disabled={!locationToAdd} aria-label="Agregar ubicación">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="users-admin__access-table">
                      <div className="users-admin__access-row users-admin__access-row--head">
                        <span>Ubicación</span>
                        <span>Acciones</span>
                      </div>
                      {selectedLocations.map((location) => (
                        <div className="users-admin__access-row" key={location.id}>
                          <strong>{location.name}</strong>
                          <button type="button" className="users-admin__access-delete" onClick={() => confirmRemoveLocationAccess(location)} aria-label={`Eliminar ${location.name}`}>
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      ))}
                      {!selectedLocations.length ? <p>No hay ubicaciones agregadas.</p> : null}
                    </div>
                  </section>
                </div>
              )}
            </div>

            <div className="users-admin__modal-footer">
              <div className="users-admin__modal-footer-left">
                <button
                  type="button"
                  className={`users-admin__switch ${form.isActive !== false ? "is-on" : ""}`}
                  onPointerUp={(event) => handleSwitchPointerUp(event, () => updateField("isActive", form.isActive === false))}
                  onKeyDown={(event) => handleSwitchKeyDown(event, () => updateField("isActive", form.isActive === false))}
                  aria-pressed={form.isActive !== false}
                >
                  <span />
                </button>
                <span className="users-admin__switch-caption">Habilitado</span>
                {message ? <p className="users-admin__message">{message}</p> : null}
              </div>
              <button type="button" className="users-admin__secondary-button" onClick={closeEditModal}>Cancelar</button>
              <button type="submit">{modalMode === "create" ? "Crear y enviar claim" : "Guardar cambios"}</button>
            </div>
          </form>
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
      {moduleDropdownMenu}
      {locationDropdownMenu}
      {roleDropdownMenu}
    </div>
  );
}

export default UsersAdmin;
